import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import { createRepository } from '@/repositories/GenericRepository';
import PaginatedResponse from '@/types/paginatedResponse';
import { PersonAvailability } from '@/types/personAvailability';
import { QueryParams } from '@/types/query';
import RepositoryResponse from '@/types/repositoryResponse';
import ajax from '@/utils/ajax';

const basePath = '/api/availability';

export const personAvailabilityRepository = createRepository<
  PersonAvailability,
  PersonAvailability
>(basePath);

const PersonAvailabilityRepository = () => {
  const { host } = useContext(AppContext);

  return {
    async list(
      params: QueryParams = {}
    ): Promise<RepositoryResponse<PaginatedResponse<PersonAvailability[]>>> {
      const response = await ajax.get({
        url: `${host}${basePath}/`,
        params
      });

      return {
        data: response.data as PaginatedResponse<PersonAvailability[]>,
        isError: false
      };
    },

    async get({ id }: { id: string }): Promise<RepositoryResponse<PersonAvailability>> {
      const response = await ajax.get({
        url: `${host}${basePath}/${id}/`
      });

      return {
        data: response.data,
        isError: false
      };
    },

    async create({
      data
    }: {
      data: Partial<PersonAvailability>;
    }): Promise<RepositoryResponse<PersonAvailability>> {
      const response = await ajax.post({
        url: `${host}${basePath}/`,
        data: data as Record<string, unknown>
      });

      return {
        data: response.data as unknown as PersonAvailability,
        isError: false
      };
    },

    async update({
      id,
      data
    }: {
      id: string;
      data: Partial<PersonAvailability>;
    }): Promise<RepositoryResponse<PersonAvailability>> {
      const response = await ajax.put({
        url: `${host}${basePath}/${id}/`,
        data: data as Record<string, unknown>
      });

      return {
        data: response.data as unknown as PersonAvailability,
        isError: false
      };
    },

    async delete({ id }: { id: string }): Promise<RepositoryResponse<Record<string, never>>> {
      await ajax.delete({
        url: `${host}${basePath}/${id}/`
      });

      return {
        data: {},
        isError: false
      };
    },

    async listByPersonId({
      personId,
      params = {}
    }: {
      personId: string;
      params?: QueryParams;
    }): Promise<RepositoryResponse<PaginatedResponse<PersonAvailability[]>>> {
      return this.list({ person_id: personId, ...params });
    }
  };
};

export default PersonAvailabilityRepository;
